import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import useUnitProvider from '/@/core/context/unit';
import { useReports } from '/@/hooks/reports';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { formatDate } from '/@/utils';

/** ± window (in months) around the campaign planting date used to match / limit
 * the FieldView planting date, per the V2 spec. */
export const PLANTING_WINDOW_MONTHS = 1;
/** Max span (in months) after the planting date to look for a harvest report. */
export const HARVEST_WINDOW_MONTHS = 12;

/** FieldView crop name per Experto crop. Only Corn is matched today (mirrors V1);
 * crops without a mapping fall back to the date window alone. */
const CROP_FV_NAME: Partial<Record<CropMonitoring['crop'], string>> = {
    corn: 'Corn',
};

export type FormValues = {
    plantingDate: Dayjs | null;
    plantingResultId: number | null;
    plantingRequestId: number | null;
    harvestDate: Dayjs | null;
    harvestResultId: number | null;
    harvestRequestId: number | null;
    yield: number | null;
    humidity: number | null;
};

export type DateOption = {
    value: number;
    label: string;
    date: Dayjs;
    requestId: number;
};

export type PlantingDateBounds = {
    minDate: Dayjs | undefined;
    maxDate: Dayjs | undefined;
};

/** Minimal shapes of the FieldView report payloads consumed here (the SWR
 * `useReports` response is otherwise untyped). */
type FvHarvestResult = {
    id: number;
    report: number;
    cropName: string;
    harvestDates: string[];
    totalHarvestedArea: number;
    climateCalculatedAverageYield: number;
    totalWetMass: number;
    totalMoistureMass: number;
};
type FvPlantingResult = {
    id: number;
    report: number;
    cropName: string;
    plantingDate: string;
    plantedArea: number;
    seedProductNames: string[];
};
type FvReportRow = { status?: string; results?: FvHarvestResult[]; mergedResults?: FvPlantingResult[] };

const rowsOf = (query: ReturnType<typeof useReports>): FvReportRow[] => (query.data?.results as FvReportRow[]) || [];
const someRowPending = (query: ReturnType<typeof useReports>) => rowsOf(query).some((r) => r.status === 'pending');

const usePendingReportsRetry = (query: ReturnType<typeof useReports>, intervalMs = 5000) => {
    const isPending = someRowPending(query);
    const { mutate } = query;
    useEffect(() => {
        if (!isPending) return;
        const interval = setInterval(() => {
            mutate();
        }, intervalMs);
        return () => clearInterval(interval);
    }, [isPending, mutate, intervalMs]);
};

/** ±PLANTING_WINDOW_MONTHS window around the campaign planting date. */
export const getPlantingDateBounds = (anchor: Dayjs | null | undefined): PlantingDateBounds => {
    if (!anchor || !anchor.isValid()) {
        return { minDate: undefined, maxDate: undefined };
    }
    return {
        minDate: anchor.subtract(PLANTING_WINDOW_MONTHS, 'month'),
        maxDate: anchor.add(PLANTING_WINDOW_MONTHS, 'month'),
    };
};

const matchesCrop = (cropName: string, crop: CropMonitoring['crop']): boolean => {
    const fvName = CROP_FV_NAME[crop];
    if (!fvName) return true; // no mapping → don't exclude by crop
    return cropName === fvName;
};

export const useReportForm = ({
    field,
    cropMonitoring,
    isFieldviewType,
    enabled,
    reportsMutating,
    reportsError,
}: {
    field: Field;
    cropMonitoring: CropMonitoring;
    isFieldviewType: boolean;
    /** Only fetch FieldView reports while the modal is open. */
    enabled: boolean;
    reportsMutating: boolean;
    reportsError: unknown;
}) => {
    const units = useUnitProvider();

    /** The campaign planting date is the anchor for both windows. */
    const plantingAnchor = useMemo(
        () => (cropMonitoring?.plantingDate ? dayjs(cropMonitoring.plantingDate) : null),
        [cropMonitoring?.plantingDate]
    );
    const plantingDateBounds = useMemo(() => getPlantingDateBounds(plantingAnchor), [plantingAnchor]);

    /** CM created from a FieldView planting report → planting date is fixed. */
    const plantingDisabled = cropMonitoring?.sourcePlantingReport != null;

    const form = useForm<FormValues>({
        defaultValues: {
            humidity: 14.5,
            plantingDate: plantingAnchor,
            plantingRequestId: cropMonitoring?.sourcePlantingReport ?? null,
        },
    });

    // --- Harvest options: FieldView harvest results within [planting, planting + 1y] ---
    const harvestReportsQuery = useReports({ fieldId: field.id, type: 'harvest' }, { enabled });
    usePendingReportsRetry(harvestReportsQuery);

    const validHarvestResults = useMemo<FvHarvestResult[]>(() => {
        if (!plantingAnchor) return [];
        const windowEnd = plantingAnchor.add(HARVEST_WINDOW_MONTHS, 'month');
        return rowsOf(harvestReportsQuery).flatMap((report) =>
            (report.results || []).filter((result) => {
                if (!matchesCrop(result.cropName, cropMonitoring.crop)) return false;
                const harvestDate = dayjs(result.harvestDates[0]);
                return !harvestDate.isBefore(plantingAnchor, 'day') && !harvestDate.isAfter(windowEnd, 'day');
            })
        );
    }, [harvestReportsQuery, plantingAnchor, cropMonitoring.crop]);

    const harvestDateOptions = useMemo<DateOption[]>(() => {
        const today = dayjs();
        return validHarvestResults
            .map(
                (result): DateOption => ({
                    value: result.id,
                    label: `${formatDate(dayjs(result.harvestDates[0]).toDate())} (${_('Area cosechada:')} ${units.toArea(result.totalHarvestedArea, 2).toString()})`,
                    date: dayjs(result.harvestDates[0]),
                    requestId: result.report,
                })
            )
            .toSorted((a, b) => Math.abs(a.date.diff(today, 'day')) - Math.abs(b.date.diff(today, 'day')));
    }, [validHarvestResults, units]);

    // --- Planting options: FieldView planting results within ±1 month of anchor ---
    const plantingReportsQuery = useReports({ fieldId: field.id, type: 'planting' }, { enabled });
    usePendingReportsRetry(plantingReportsQuery);

    const validPlantingResults = useMemo<FvPlantingResult[]>(() => {
        return rowsOf(plantingReportsQuery).flatMap((report) =>
            (report.mergedResults || []).filter((result) => {
                if (!matchesCrop(result.cropName, cropMonitoring.crop)) return false;
                if (!plantingDateBounds.minDate || !plantingDateBounds.maxDate) return true;
                const plantingDate = dayjs(result.plantingDate);
                return (
                    !plantingDate.isBefore(plantingDateBounds.minDate, 'day') &&
                    !plantingDate.isAfter(plantingDateBounds.maxDate, 'day')
                );
            })
        );
    }, [plantingReportsQuery, plantingDateBounds, cropMonitoring.crop]);

    const plantingDateOptions = useMemo<DateOption[]>(() => {
        const anchor = plantingAnchor;
        return validPlantingResults
            .map(
                (result): DateOption => ({
                    value: result.id,
                    label: `${formatDate(dayjs(result.plantingDate).toDate())} (${_('Area plantada')}: ${units.toArea(result.plantedArea, 2).toString()}) (${result.seedProductNames.join(', ')})`,
                    date: dayjs(result.plantingDate),
                    requestId: result.report,
                })
            )
            .toSorted((a, b) => {
                if (!anchor) return 0;
                return Math.abs(a.date.diff(anchor, 'day')) - Math.abs(b.date.diff(anchor, 'day'));
            });
    }, [validPlantingResults, plantingAnchor, units]);

    // --- Auto-select nearest harvest option ---
    useEffect(() => {
        const nearest = harvestDateOptions[0];
        if (nearest) {
            form.setValue('harvestDate', nearest.date);
            form.setValue('harvestResultId', nearest.value);
            form.setValue('harvestRequestId', nearest.requestId);
        }
    }, [harvestDateOptions, form]);

    // --- Auto-select nearest planting option (fixed CMs keep their planting date) ---
    useEffect(() => {
        if (plantingDisabled) return;
        const nearest = plantingDateOptions[0];
        if (nearest) {
            form.setValue('plantingDate', nearest.date);
            form.setValue('plantingResultId', nearest.value);
            form.setValue('plantingRequestId', nearest.requestId);
        }
    }, [plantingDateOptions, plantingDisabled, form]);

    // --- Yield & humidity prefilled from the selected harvest result ---
    const harvestResultId = form.watch('harvestResultId');
    useEffect(() => {
        const result = validHarvestResults.find((r) => r.id === harvestResultId);
        if (result) {
            form.setValue('yield', units.toYield(result.climateCalculatedAverageYield, 'corn', 2).value);
            // Humidity is a percentage of the wet mass, so moisture over wet mass — not
            // the other way round.
            form.setValue(
                'humidity',
                result.totalWetMass ? round((result.totalMoistureMass / result.totalWetMass) * 100, 2) : null
            );
        }
    }, [harvestResultId, validHarvestResults, form, units]);

    const reportsPending = useMemo(() => {
        if (!isFieldviewType) return false;
        return (
            someRowPending(harvestReportsQuery) ||
            someRowPending(plantingReportsQuery) ||
            reportsMutating ||
            (!harvestReportsQuery.data && !reportsError)
        );
    }, [isFieldviewType, harvestReportsQuery, plantingReportsQuery, reportsMutating, reportsError]);

    const hasHarvestData = harvestDateOptions.length > 0;
    const harvestDataResolved = isFieldviewType
        ? !reportsPending && (Boolean(harvestReportsQuery.data) || Boolean(reportsError))
        : true;

    /** Both dates resolved from FieldView → we're confirming, not entering, data. */
    const plantingResultId = form.watch('plantingResultId');
    const bothAutoSelected = Boolean(plantingResultId && harvestResultId);

    return {
        form,
        units,
        plantingDateOptions,
        harvestDateOptions,
        plantingDateBounds,
        plantingDisabled,
        bothAutoSelected,
        reportsPending,
        hasHarvestData,
        harvestDataResolved,
    };
};
